import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { callOutcomes } from '../mocks/calls';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import CallTranscript from '../components/CallTranscript';

function formatDuration(seconds) {
  if (!seconds || Number.isNaN(seconds)) return '—';
  const total = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function formatDateTime(value) {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString();
}

function toDisplayText(value, fallback = '—') {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => toDisplayText(item, '')).filter(Boolean).join(', ') || fallback;
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return fallback;
}

function getOutcomeText(call, t) {
  const outcomeData = call?.call_outcome ?? call?.outcome;
  let key, rawLabel;

  if (outcomeData && typeof outcomeData === 'object') {
    key = outcomeData.outcome;
    rawLabel = outcomeData.outcome_label ?? outcomeData.outcome;
  } else {
    key = outcomeData;
    rawLabel = outcomeData;
  }

  if (!key && rawLabel == null) return '—';

  // i18n lookup via callOutcomes map (display layer only — raw key untouched)
  if (t && key && callOutcomes[key]) {
    const fallback = rawLabel != null ? String(rawLabel).replace(/\.$/, '').trim() : String(key);
    return t(callOutcomes[key].labelKey, { defaultValue: fallback });
  }

  // Fallback: strip trailing period from raw label
  if (rawLabel != null && rawLabel !== '') {
    return String(rawLabel).replace(/\.$/, '').trim() || '—';
  }

  return '—';
}

function cleanSummaryText(value) {
  return String(value ?? '')
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .trim();
}

function getSummaryText(call, t) {
  const summary = call?.summary;
  const fallback = t('callHistory.noSummary', 'No summary');

  if (summary && typeof summary === 'object') {
    return cleanSummaryText(toDisplayText(summary.summary ?? summary.outcome_label ?? summary.outcome ?? summary, fallback)) || fallback;
  }

  return cleanSummaryText(toDisplayText(summary, fallback)) || fallback;
}

function getSentiment(call) {
  if (call?.summary && typeof call.summary === 'object' && call.summary.sentiment) {
    return toDisplayText(call.summary.sentiment, '—');
  }
  if (call?.call_outcome && typeof call.call_outcome === 'object' && call.call_outcome.sentiment) {
    return toDisplayText(call.call_outcome.sentiment, '—');
  }
  return toDisplayText(call?.sentiment, '—');
}

function getOutcomeBadgeClass(outcome) {
  const normalized = (outcome || '').toLowerCase();

  // Positive outcomes (sales + cobranza)
  if (/(cita agendada|test drive agendado|interesado|promesa de pago|pago realizado|appointment|test_drive|interested|payment_promise|paid)/.test(normalized)) {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300';
  }

  // Negative outcomes
  if (/(no interesado|no puede pagar|número equivocado|numero equivocado|not interested|cannot pay|wrong number)/.test(normalized)) {
    return 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300';
  }

  // Follow-up / callback
  if (/(llamar después|llamar despues|callback|call back|follow up|devolver llamada|later)/.test(normalized)) {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  }

  // No contact / unresolved
  if (/(no contestó|no contesto|no answer|unanswered|busy|voicemail|sin respuesta|colgó sin resolución|colgo sin resolucion|hung up)/.test(normalized)) {
    return 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
  }

  return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
}

// Oct 16: transcript normalization + bubbles live in the shared <CallTranscript> component so the
// Calendar "Llamadas pendientes" detail renders the same view (see components/CallTranscript.js).

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getPresetDates(preset) {
  const now = new Date();
  const end = new Date(now);

  if (preset === 'today') {
    return {
      date_from: toDateInputValue(now),
      date_to: toDateInputValue(end),
    };
  }

  if (preset === 'this_week') {
    const start = new Date(now);
    const mondayOffset = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - mondayOffset);

    return {
      date_from: toDateInputValue(start),
      date_to: toDateInputValue(end),
    };
  }

  if (preset === 'this_month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      date_from: toDateInputValue(start),
      date_to: toDateInputValue(end),
    };
  }

  return {
    date_from: '',
    date_to: '',
  };
}

export default function CallHistory() {
  const { t } = useTranslation();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState('this_month');
  const [detailModal, setDetailModal] = useState({
    open: false,
    loading: false,
    call: null,
  });
  const [filters, setFilters] = useState({
    phone: '',
    date_from: '',
    date_to: '',
  });
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-link one-shot guard (feat-calendar-ux-sep3): act on a given ?callSid
  // once so the URL-strip doesn't re-open the modal.
  const deepLinkSidRef = useRef(null);

  const fetchCalls = async () => {
    try {
      setLoading(true);
      const params = {
        limit: 100,
      };

      if (filters.phone.trim()) params.phone = filters.phone.trim();
      if (filters.date_from) params.date_from = new Date(filters.date_from).toISOString();
      if (filters.date_to) {
        const end = new Date(filters.date_to);
        end.setHours(23, 59, 59, 999);
        params.date_to = end.toISOString();
      }

      const res = await api.get('/calls/history', { params });
      setCalls(Array.isArray(res.data?.calls) ? res.data.calls : []);
    } catch (err) {
      console.error('Failed to load call history:', err);
      toast.error(t('errors.failedLoadCalls', 'Failed to load calls'));
      setCalls([]);
    } finally {
      setLoading(false);
    }
  };

  const openCallDetail = async (call) => {
    setDetailModal({ open: true, loading: true, call });
    try {
      const res = await api.get(`/calls/${call.call_sid}/detail`);
      setDetailModal({ open: true, loading: false, call: res.data || call });
    } catch (err) {
      console.error('Failed to load call detail:', err);
      const status = err.response?.status;
      if (status === 404) {
        // Call exists but no transcript yet — render empty-state, no toast
        setDetailModal({ open: true, loading: false, call: { ...call, transcript: [] } });
      } else if (status === 403) {
        toast.error(t('errors.accessDenied', 'Access denied'));
        setDetailModal({ open: true, loading: false, call });
      } else {
        // 5xx or network error — toast and fall back to cached call data
        toast.error(t('errors.failedLoadTranscript', 'Failed to load transcript'));
        setDetailModal({ open: true, loading: false, call });
      }
    }
  };

  useEffect(() => {
    if (datePreset === 'custom') return;
    setFilters((prev) => ({
      ...prev,
      ...getPresetDates(datePreset),
    }));
  }, [datePreset]);

  useEffect(() => {
    fetchCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.date_from, filters.date_to]);

  // Deep-link from the Calendar cita panel: /call-history?callSid=<sid>
  // (feat-calendar-ux-sep3). "Ver llamada" must open the EXACT call detail
  // (transcript + recording), not just land on the list. The call may be
  // outside the current date-preset page, so we fetch its detail directly by
  // SID instead of relying on it being present in calls[]. Unknown/failed SID
  // -> toast (never a silent dead-click). One-shot per SID; strips the param.
  useEffect(() => {
    const sid = (searchParams.get('callSid') || '').trim();
    if (!sid) return;
    if (deepLinkSidRef.current === sid) return;
    deepLinkSidRef.current = sid;

    (async () => {
      setDetailModal({ open: true, loading: true, call: { call_sid: sid } });
      try {
        const res = await api.get(`/calls/${encodeURIComponent(sid)}/detail`);
        setDetailModal({ open: true, loading: false, call: res.data || { call_sid: sid } });
      } catch (err) {
        const status = err.response?.status;
        if (status === 404) {
          setDetailModal({ open: false, loading: false, call: null });
          toast.error(t('calls.callNotFound', 'Call not found'));
        } else if (status === 403) {
          setDetailModal({ open: false, loading: false, call: null });
          toast.error(t('errors.accessDenied', 'Access denied'));
        } else {
          setDetailModal({ open: false, loading: false, call: null });
          toast.error(t('errors.failedLoadTranscript', 'Failed to load transcript'));
        }
      }
    })();

    const next = new URLSearchParams(searchParams);
    next.delete('callSid');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    fetchCalls();
  };

  const detailCall = detailModal.call;
  const detailSummary = getSummaryText(detailCall || {}, t);
  const detailOutcome = getOutcomeText(detailCall || {}, t);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">{t('callHistory.title')}</h1>
        <p className="text-sm text-muted mt-1">{t('callHistory.simpleSubtitle', 'Todas las llamadas, más recientes primero')}</p>
      </div>

      <form onSubmit={onSearchSubmit} className="card p-4 space-y-4">
        <div>
          <label className="block text-xs text-muted mb-2">{t('callHistory.dateRange', 'Rango de fechas')}</label>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'today', label: t('callHistory.today', 'Hoy') },
              { key: 'this_week', label: t('callHistory.thisWeek', 'Esta semana') },
              { key: 'this_month', label: t('callHistory.thisMonth', 'Este mes') },
              { key: 'custom', label: t('callHistory.custom', 'Personalizado') },
            ].map((option) => (
              <button
                key={option.key}
                type="button"
                className={`btn ${datePreset === option.key ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setDatePreset(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full md:flex-1 md:min-w-[280px]">
            <label className="block text-xs text-muted mb-1">{t('callHistory.caller', 'Phone')}</label>
            <input
              className="input-field"
              placeholder={t('callHistory.searchPlaceholder')}
              value={filters.phone}
              onChange={(e) => setFilters((prev) => ({ ...prev, phone: e.target.value }))}
            />
          </div>

          <div className="w-full sm:w-auto">
            <label className="block text-xs text-muted mb-1">{t('callHistory.from')}</label>
            <input
              type="date"
              className="input-field"
              value={filters.date_from}
              onChange={(e) => {
                setDatePreset('custom');
                setFilters((prev) => ({ ...prev, date_from: e.target.value }));
              }}
            />
          </div>

          <div className="w-full sm:w-auto">
            <label className="block text-xs text-muted mb-1">{t('callHistory.to')}</label>
            <input
              type="date"
              className="input-field"
              value={filters.date_to}
              onChange={(e) => {
                setDatePreset('custom');
                setFilters((prev) => ({ ...prev, date_to: e.target.value }));
              }}
            />
          </div>

          <button type="submit" className="btn btn-primary shrink-0 self-end flex items-center gap-1">
            <MagnifyingGlassIcon className="h-4 w-4" />
            {t('common.search', 'Search')}
          </button>
        </div>
      </form>

      <div className="card overflow-visible border border-border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-secondary/60">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted uppercase tracking-wide">{t('callHistory.tableHeaderDateTime', 'FECHA/HORA')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted uppercase tracking-wide">{t('callHistory.tableHeaderPhone', 'TELÉFONO')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted uppercase tracking-wide">{t('callHistory.tableHeaderAgent', 'AGENTE')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted uppercase tracking-wide">{t('callHistory.tableHeaderDuration', 'DURACIÓN')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted uppercase tracking-wide">{t('callHistory.tableHeaderSummary', 'RESUMEN')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted uppercase tracking-wide">{t('callHistory.tableHeaderOutcome', 'RESULTADO')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted">{t('common.loading')}</td>
                </tr>
              ) : calls.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted">{t('callHistory.noCalls')}</td>
                </tr>
              ) : (
                calls.map((call, index) => {
                  const outcomeText = getOutcomeText(call, t);
                  const summaryText = getSummaryText(call, t);

                  return (
                    <tr
                      key={call.call_sid}
                      role="button"
                      tabIndex={0}
                      onClick={() => openCallDetail(call)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openCallDetail(call);
                        }
                      }}
                      className={`cursor-pointer transition-colors ${
                        index % 2 === 0 ? 'bg-card' : 'bg-background/40'
                      } hover:bg-primary-50 dark:hover:bg-primary-900/10`}
                    >
                      <td className="px-4 py-4 text-sm text-foreground">{formatDateTime(call.start_time)}</td>
                      <td className="px-4 py-4 text-sm text-foreground">{toDisplayText(call.phone_number, '—')}</td>
                      <td className="px-4 py-4 text-sm text-foreground">{toDisplayText(call.agent_name, '—')}</td>
                      <td className="px-4 py-4 text-sm text-foreground">{formatDuration(call.duration)}</td>
                      <td className="px-4 py-4 text-sm text-foreground max-w-lg">
                        <div className="relative group">
                          <div
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {summaryText}
                          </div>
                          <div className="pointer-events-none absolute left-0 bottom-full mb-2 hidden w-[28rem] max-w-[70vw] rounded-md border border-border bg-card px-3 py-2 text-xs text-foreground shadow-lg z-50 group-hover:block">
                            {summaryText}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-foreground">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${getOutcomeBadgeClass(outcomeText)}`}>
                          {outcomeText}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detailModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDetailModal({ open: false, loading: false, call: null })}>
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground">{t('calls.callTranscript', 'Call Transcript')}</h3>
                <p className="text-xs text-muted">{toDisplayText(detailCall?.call_sid, '—')}</p>
              </div>
              <button
                className="rounded-full p-1 text-muted hover:bg-secondary"
                onClick={() => setDetailModal({ open: false, loading: false, call: null })}
                aria-label={t('common.close', 'Close')}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {detailModal.loading ? (
              <div className="px-6 py-10 text-center text-sm text-muted">{t('calls.loadingTranscript', 'Loading transcript...')}</div>
            ) : (
              <div className="space-y-5 p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl border border-border bg-background/50 p-3">
                    <div className="text-xs text-muted mb-1">{t('callHistory.caller', 'Phone')}</div>
                    <div className="font-medium text-foreground">{toDisplayText(detailCall?.phone_number, '—')}</div>
                  </div>
                  <div className="rounded-xl border border-border bg-background/50 p-3">
                    <div className="text-xs text-muted mb-1">{t('callHistory.agent')}</div>
                    <div className="font-medium text-foreground">{toDisplayText(detailCall?.agent_name, '—')}</div>
                  </div>
                  <div className="rounded-xl border border-border bg-background/50 p-3">
                    <div className="text-xs text-muted mb-1">{t('callHistory.durationCol')}</div>
                    <div className="font-medium text-foreground">{formatDuration(detailCall?.duration)}</div>
                  </div>
                  <div className="rounded-xl border border-border bg-background/50 p-3">
                    <div className="text-xs text-muted mb-1">{t('callHistory.outcome')}</div>
                    <div className="font-medium text-foreground">{detailOutcome}</div>
                  </div>
                  <div className="rounded-xl border border-border bg-background/50 p-3 md:col-span-2">
                    <div className="text-xs text-muted mb-1">{t('callHistory.dateTime')}</div>
                    <div className="font-medium text-foreground">{formatDateTime(detailCall?.start_time)}</div>
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-background/50 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-foreground">{t('calls.callSummary', 'Call Summary')}</h4>
                    <span className="text-xs text-muted">
                      {t('campaigns.sentiment', 'Sentiment')}: <span className="text-foreground font-medium">{getSentiment(detailCall || {})}</span>
                    </span>
                  </div>
                  <div className="text-sm text-foreground whitespace-pre-wrap">{detailSummary}</div>
                </div>

                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="border-b border-border bg-secondary/50 px-4 py-2 text-sm font-semibold text-foreground">
                    {t('callHistory.transcript', 'Transcript')}
                  </div>

                  <CallTranscript transcript={detailCall?.transcript} agentName={detailCall?.agent_name} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
